import React from "react";
import { SearchResponse } from "./appSearchApi";
import { SearchRowItem, rowKey } from "./searchRows";
import { SearchResultRow } from "./SearchResultRow";
import { TableSchemaPopover } from "./TableSchemaPopover";

interface SearchResultsPanelProps {
  results: SearchResponse | null;
  isSearching: boolean;
  error: string | null;
  rows: SearchRowItem[];
  activeKey: string | null;
  onHoverKey: (key: string | null) => void;
  /** Parent-match cue inputs, derived in AppSearchBox. */
  washedModelId: string | null;
  modelIdsInResults: ReadonlySet<string>;
}

const BUCKET_LABEL: Record<SearchRowItem["kind"], string> = {
  model: "Models",
  table: "Tables",
  dataset: "Datasets",
};

export const SearchResultsPanel: React.FC<SearchResultsPanelProps> = ({
  results,
  isSearching,
  error,
  rows,
  activeKey,
  onHoverKey,
  washedModelId,
  modelIdsInResults,
}) => {
  const isEmpty =
    results !== null &&
    results.models.length === 0 &&
    results.tables.length === 0 &&
    results.datasets.length === 0;

  const announcement = results
    ? isEmpty
      ? "No results"
      : `${results.models.length} models, ${results.tables.length} tables, ${results.datasets.length} datasets`
    : "";

  const renderBucket = (kind: SearchRowItem["kind"]): React.ReactNode => {
    const bucketRows = rows.filter((row) => row.kind === kind);
    if (bucketRows.length === 0) return null;
    return (
      <div key={kind}>
        <div className="px-2 pt-2 pb-1 text-small text-fg-muted">
          {BUCKET_LABEL[kind]} · {bucketRows.length}
        </div>
        {bucketRows.map((item) => {
          const row = (
            <SearchResultRow
              item={item}
              isActive={activeKey === rowKey(item)}
              isParentWashed={
                item.kind === "model" && item.model.id === washedModelId
              }
              parentIsHit={
                item.kind === "table" &&
                item.table.parent_model.name !== null &&
                modelIdsInResults.has(item.table.parent_model.id)
              }
              onHover={() => onHoverKey(rowKey(item))}
            />
          );
          return item.kind === "table" ? (
            <TableSchemaPopover key={rowKey(item)} table={item.table}>
              {row}
            </TableSchemaPopover>
          ) : (
            <React.Fragment key={rowKey(item)}>{row}</React.Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <div onMouseLeave={() => onHoverKey(null)}>
      <div
        className={`app-search-progress ${isSearching ? "is-searching" : ""}`}
      />
      <div className="sr-only" aria-live="polite">
        {announcement}
      </div>
      <div
        id="app-search-listbox"
        role="listbox"
        aria-label="Search results"
        className="max-h-96 overflow-y-auto px-1 pb-1"
      >
        {results === null && isSearching && (
          <div className="px-2 py-3 text-small text-fg-muted">Searching…</div>
        )}
        {isEmpty && !isSearching && (
          <div className="px-2 py-3 text-small text-fg-muted text-center">
            No results — models, tables, and datasets are all searched
          </div>
        )}
        {renderBucket("model")}
        {renderBucket("table")}
        {renderBucket("dataset")}
        {error && results !== null && (
          <div className="px-2 py-2 text-small text-fg-muted">
            Search is unavailable right now
          </div>
        )}
      </div>
    </div>
  );
};
